import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenantContext } from '@/shared/providers/TenantProvider';
import { SEGMENT_MAP, SINGULAR_MAP, isDynamicId } from '@/shared/constants/pageTitleSegments';

/**
 * usePageTitle
 *
 * Automatically derives a descriptive browser tab title from the current URL
 * and sets `document.title` on every route change.
 *
 * Title format:
 *   - List pages:   "Trips - Ecolane Dispatch"
 *   - Detail pages: "Trip 12345 - Ecolane Dispatch"
 *   - Sub-pages:    "Schedule Versions - Ecolane Dispatch"
 *   - Dashboard:    "Dashboard - Ecolane Dispatch"
 *
 * The app name suffix is sourced from the tenant's `branding.appName` config,
 * falling back to "Ecolane Dispatch" before the tenant config has loaded.
 *
 * @returns The computed page label (the part before the " - <appName>" suffix),
 *          useful for testing or rendering in <title> tags directly.
 */
export const usePageTitle = (): string => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { tenant } = useTenantContext();

  const appName: string = tenant?.branding?.appName ?? 'Ecolane Dispatch';

  const pageLabel = derivePageLabel(pathname, t);
  const fullTitle = pageLabel ? `${pageLabel} - ${appName}` : appName;

  useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle]);

  return pageLabel;
};

/**
 * Pure function that derives a human-readable page label from a pathname.
 * Exported so it can be unit-tested independently of the React hook.
 *
 * Algorithm (right-to-left segment scan):
 *
 * 1. Split pathname into non-empty segments.
 * 2. Strip trailing sub-page qualifiers that add no entity context
 *    (history, edit, versions, details, events, stops, return, rebook, copy).
 * 3. If the rightmost meaningful segment is a numeric/UUID ID:
 *    a. Look up the segment immediately before the ID in SINGULAR_MAP.
 *    b. If found → "Entity ID" (e.g. "Trip 12345").
 *    c. If not found → fall back to the segment label from SEGMENT_MAP.
 * 4. Otherwise use the rightmost meaningful segment mapped via SEGMENT_MAP.
 * 5. If nothing maps, return the raw segment capitalised.
 */
export const derivePageLabel = (
  pathname: string,
  t: (key: string, fallback: string) => string,
): string => {
  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);

  if (segments.length === 0) return '';

  // Sub-page qualifiers that should be stripped when building the title
  // (we keep their parent entity as the primary identifier).
  const TRAILING_QUALIFIERS = new Set([
    'history', 'edit', 'versions', 'details', 'events',
    'stops', 'return', 'rebook', 'copy', 'print-manifest',
    'service-rules', 'funding-eligibility',
  ]);

  // Walk backwards through segments, skipping trailing qualifiers first.
  // We keep track of qualifiers that were stripped so we can append them
  // when there is no ID present (e.g. "/operations/schedules/versions" → "Schedule versions").
  const strippedQualifiers: string[] = [];
  let tail = segments.length - 1;

  while (tail > 0 && TRAILING_QUALIFIERS.has(segments[tail].toLowerCase())) {
    strippedQualifiers.unshift(segments[tail].toLowerCase());
    tail--;
  }

  const tailSegment = segments[tail];
  const tailLower = tailSegment.toLowerCase();

  // ── Case 1: Tail is a dynamic ID ──────────────────────────────────────────
  if (isDynamicId(tailSegment)) {
    const parentSegment = tail > 0 ? segments[tail - 1].toLowerCase() : '';
    const singular = SINGULAR_MAP[parentSegment];

    if (singular) {
      const singularLabel = t(singular.i18nKey, singular.fallback);
      return `${singularLabel} ${tailSegment}`;
    }

    // Parent segment not in SINGULAR_MAP — fall back to parent label
    const parentMapping = SEGMENT_MAP[parentSegment];
    if (parentMapping) {
      return t(parentMapping.i18nKey, parentMapping.fallback);
    }

    // Nothing maps — return raw ID
    return tailSegment;
  }

  // ── Case 2: Tail is a known segment ──────────────────────────────────────
  const mapping = SEGMENT_MAP[tailLower];
  if (mapping) {
    const label = t(mapping.i18nKey, mapping.fallback);

    // If qualifiers were stripped AND tail is not a nav section itself,
    // append the first qualifier for extra context
    // e.g. /operations/schedules/:id/versions → tail after strip = schedule ID
    // (that case is handled above). Here we handle cases like
    // /operations/archive-trips/reconcile where "reconcile" stays as-is.
    return label;
  }

  // ── Case 3: Unrecognised segment — capitalise raw value ──────────────────
  return tailSegment.charAt(0).toUpperCase() + tailSegment.slice(1);
};

export default usePageTitle;